/**
 * useDiscoverChannels — S21
 *
 * Wraps getPublicChannels() with React Query useInfiniteQuery.
 * Consumes the ChannelDiscoveryResponse envelope from S20:
 *   { items, total, page, page_size, has_more }
 *
 * Using useInfiniteQuery gives a clean "load more" pattern that accumulates
 * pages in the React Query cache without any useEffect-based state mutations.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { getPublicChannels } from '@/services/channelApi';
import type { GetPublicChannelsParams } from '@/services/channelApi';
import type { ChannelDiscoveryResponse } from '@/types/channel';

export type DiscoverChannelsParams = Omit<GetPublicChannelsParams, 'page'>;

export function useDiscoverChannels(params?: DiscoverChannelsParams) {
  return useInfiniteQuery<ChannelDiscoveryResponse>({
    queryKey: [
      'discover-channels',
      params?.q ?? '',
      params?.sport_focus ?? '',
      params?.sort ?? 'newest',
      params?.page_size ?? 20,
    ],
    queryFn: ({ pageParam }) =>
      getPublicChannels({ ...params, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.page + 1 : undefined,
    staleTime: 30_000, // 30 s — discovery list changes infrequently
  });
}
